import React, { useState, useRef, useEffect } from "react";
import { Bot, Sparkles, Zap, Cpu, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface AIModel {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  activeBgColor: string;
  borderColor: string;
  activeBorderColor: string;
  description?: string;
}

interface ModelButtonSelectorProps {
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  className?: string;
  isLoading?: boolean;
  loadingModelId?: string | null;
}

// ModelTooltip component for showing model descriptions
const ModelTooltip: React.FC<{
  children: React.ReactNode;
  description: string;
}> = ({ children, description }) => {
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="w-full"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        {children}
      </div>
      {visible && (
        <div
          ref={tooltipRef}
          className="absolute left-1/2 top-full mt-2 -translate-x-1/2 mb-2 w-64 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50 
          animate-fade-in-down transition-all duration-150"
        >
          <div className="p-3 text-center leading-relaxed">{description}</div>
          <div className="absolute left-1/2 top-full -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

export const availableModels: AIModel[] = [
  {
    id: "gpt-o3",
    name: "GPT O3",
    icon: Sparkles,
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    activeBgColor: "bg-green-100 dark:bg-green-800/40",
    borderColor: "border-green-200 dark:border-green-700",
    activeBorderColor: "border-green-500 dark:border-green-500",
    description:
      "Temel kavramları net adımlarla açıklar, kısa alıştırma sorularına anında yanıt verir.",
  },
  {
    id: "gpt-5",
    name: "GPT 5",
    icon: Zap,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    activeBgColor: "bg-blue-100 dark:bg-blue-800/40",
    borderColor: "border-blue-200 dark:border-blue-700",
    activeBorderColor: "border-blue-500 dark:border-blue-500",
    description:
      "Proje ödevleri ile kapsamlı sınav tekrarlarında en zengin desteği sağlar.",
  },
  {
    id: "claude-4-sonnet",
    name: "Claude 4 Sonnet",
    icon: Bot,
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    activeBgColor: "bg-purple-100 dark:bg-purple-800/40",
    borderColor: "border-purple-200 dark:border-purple-700",
    activeBorderColor: "border-purple-500 dark:border-purple-500",
    description:
      "Öğrencileri motive eden nazik yönlendirmelerle okuma-yazma etkinlikleri için idealdir.",
  },
  {
    id: "gemini-2.5-pro",
    name: "Gemini 2.5 Pro",
    icon: Cpu,
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    activeBgColor: "bg-amber-100 dark:bg-amber-800/40",
    borderColor: "border-amber-200 dark:border-amber-700",
    activeBorderColor: "border-amber-500 dark:border-amber-500",
    description:
      "Deney veya grafik yorumlama gereken STEM konularında öne çıkar.",
  },
];

export function ModelButtonSelector({
  selectedModel,
  onModelChange,
  className = "",
  isLoading = false,
  loadingModelId = null,
}: ModelButtonSelectorProps) {
  const { t } = useTranslation();

  return (
    <>
      {availableModels.map((model) => {
        const isSelected = model.id === selectedModel;
        const isCurrentLoading =
          isLoading &&
          (loadingModelId === model.id ||
            (loadingModelId === null && isSelected));

        return (
          <ModelTooltip key={model.id} description={model.description || ""}>
            <button
              onClick={() => !isLoading && onModelChange(model.id)}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium
                transition-colors w-full h-full
                ${isSelected ? `${model.activeBgColor} ${model.activeBorderColor}` : `${model.bgColor} ${model.borderColor} hover:border-gray-300 dark:hover:border-gray-600`}
                ${isLoading ? "opacity-70 cursor-not-allowed" : ""}
              `}
              role="option"
              aria-selected={isSelected}
              disabled={isLoading}
            >
              {isCurrentLoading ? (
                <Loader2
                  className={`w-3.5 h-3.5 ${model.color} animate-spin`}
                />
              ) : (
                <model.icon className={`w-3.5 h-3.5 ${model.color}`} />
              )}
              <span>{model.name}</span>
            </button>
          </ModelTooltip>
        );
      })}
    </>
  );
}
